var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import apiClient from '@utils/api-client';
import { apiInternalSubcontractorDocumentPath, apiInternalSubcontractorDocumentsPath, bulkDestroyApiInternalSubcontractorDocumentsPath, bulkUpdateApiInternalSubcontractorDocumentsPath } from '@routes';
import { setSubmitToPrimeEnabled } from '../utils/submitToPrimeButton';
/**
 * A document is scoped by BOTH its document type and the parent that owns its `target_public_id`,
 * so every call carries the parent as well as the type. The shared surface's `fetchAll` only
 * passes an owner id, which is why these are factories: the group closes over its parent.
 */
function parentParams({ parentType, parentId }) {
    return { parentType, parentId };
}
export function createFetchDocuments(parent) {
    return (documentTypeId) => __awaiter(this, void 0, void 0, function* () {
        const response = yield apiClient.get(apiInternalSubcontractorDocumentsPath(), {
            params: Object.assign(Object.assign({}, parentParams(parent)), { documentTypeId })
        });
        // Parent-level, not per-type: the server answers it from the parent's policy, so every group
        // receives the same value and the group that just mutated is the one refetching. Applied here
        // because this response is the only place it arrives — the surface renders N independent
        // groups and the button belongs to none of them.
        setSubmitToPrimeEnabled(response.data.allowedSendForReview);
        return response.data.documents;
    });
}
export function createDestroyDocument(parent) {
    return (documentTypeId, id) => __awaiter(this, void 0, void 0, function* () {
        yield apiClient.delete(apiInternalSubcontractorDocumentPath(id), {
            params: Object.assign(Object.assign({}, parentParams(parent)), { documentTypeId })
        });
    });
}
/**
 * Mass destroy. Always sends explicit ids — the endpoint treats absent and empty alike as "no
 * rows", so a cleared selection can never delete every document in the group.
 */
export function createBulkDestroyDocuments(parent) {
    return (documentTypeId, ids) => __awaiter(this, void 0, void 0, function* () {
        const response = yield apiClient.delete(bulkDestroyApiInternalSubcontractorDocumentsPath(), {
            data: { ids },
            params: Object.assign(Object.assign({}, parentParams(parent)), { documentTypeId })
        });
        return response.data;
    });
}
/**
 * The two flag writes the legacy per-row `show_on_report` and collection `mark_as_public`
 * endpoints used to perform. The server takes the action by name so both keep the policy gate
 * they had -- `mark_as_public?` checks a tenant feature flag, `show_on_report?` refuses the
 * child tenant.
 */
export const DOCUMENT_UPDATE_ACTION = {
    showOnReport: 'show_on_report',
    markAsPublic: 'mark_as_public'
};
/**
 * The prime's Documents tab addresses documents across every type on the parent at once, so
 * these take the parent alone where the subcontractor surface's factories also close over a
 * document type.
 */
export function destroyDocument(parent, id) {
    return __awaiter(this, void 0, void 0, function* () {
        yield apiClient.delete(apiInternalSubcontractorDocumentPath(id), { params: parentParams(parent) });
    });
}
export function bulkUpdateDocuments(parent, payload) {
    return __awaiter(this, void 0, void 0, function* () {
        const response = yield apiClient.patch(bulkUpdateApiInternalSubcontractorDocumentsPath(), payload, {
            params: parentParams(parent)
        });
        return response.data;
    });
}
